import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../App';
import { Check, CheckCircle, Clock, FileText, LogOut, RefreshCw, ShieldCheck, Sparkles, Upload, XCircle } from 'lucide-react';
import { extractionNotice } from '../components/VehicleDraftWizard';   // DOKUMENT-ERKENNUNG-PARTNER-001: gleiche Statussätze wie beim Fahrzeug

// Datei-Upload je Pflicht-Punkt: welche document_type(s) gehören dazu.
const UPLOADS = {
  gewerbe_or_handelsregister: [
    { type: 'gewerbeanmeldung', label: 'Gewerbeanmeldung' },
    { type: 'handelsregister',  label: 'Handelsregister' },
  ],
  personalausweis: [{ type: 'personalausweis', label: 'Personalausweis oder Aufenthaltstitel hochladen' }],
  konzession:      [{ type: 'konzession',      label: 'Konzession hochladen' }],
};

const badge = {
  approved:  { icon: <CheckCircle size={15} color="#9C27B0" />, label: 'Genehmigt',   color: '#9C27B0' },
  pending:   { icon: <Clock size={15} color="#4b5563" />,       label: 'In Prüfung',  color: '#4b5563' },
  submitted: { icon: <CheckCircle size={15} color="#9C27B0" />, label: 'Hinterlegt',  color: '#9C27B0' },
  rejected:  { icon: <XCircle size={15} color="#dc2626" />,      label: 'Abgelehnt',   color: '#dc2626' },
  missing:   { icon: <FileText size={15} color="#6b7280" />,     label: 'Fehlt',       color: '#6b7280' },
};

export const REQUIRED_PARTNER_LEGAL_TYPES = Object.freeze([
  'agb_partner',
  'beschwerde_ueberpruefung',
  'community_sicherheit',
  'null_toleranz',
  'provision_abrechnung',
  'verhalten_antidiskriminierung',
]);
// Derselbe aktivierbare SemVer-2.0-Ausschnitt wie im Backend: kein Major 0,
// keine fuehrenden Nullen und keine leeren/ungueltigen Prerelease-Identifier.
const RELEASE_VERSION_RE = /^[1-9][0-9]*\.(?:0|[1-9][0-9]*)\.(?:0|[1-9][0-9]*)(?:-(?:(?:0|[1-9][0-9]*)|(?:[0-9]*[a-z-][0-9a-z-]*))(?:\.(?:(?:0|[1-9][0-9]*)|(?:[0-9]*[a-z-][0-9a-z-]*)))*)?$/;
const SHA256_RE = /^[a-f0-9]{64}$/;

// Derselbe Zustimmungssatz im Onboarding und bei einer späteren Fassung (PartnerLegalUpdate).
export const PARTNER_LEGAL_CONSENT_TEXT = 'Ich habe alle oben aufgeführten Vertragsunterlagen gelesen und akzeptiere sie in den angezeigten Versionen. Ich stimme ausdrücklich dem variablen VOOGO-Provisionsmodell ohne fest vereinbarten Satz oder Betrag zu.';

export function normalizePartnerLegalBundle(payload) {
  if (!payload || payload.role !== 'partner' || payload.language !== 'de'
      || !RELEASE_VERSION_RE.test(String(payload.release_version || ''))
      || !Array.isArray(payload.documents)
      || payload.documents.length !== REQUIRED_PARTNER_LEGAL_TYPES.length) {
    throw new Error('invalid_partner_legal_bundle');
  }
  const seen = new Set();
  const documents = payload.documents.map((document, index) => {
    const required = ['type', 'title', 'version', 'content_hash', 'url', 'content'];
    if (!document || required.some((key) => typeof document[key] !== 'string' || !document[key].trim())) {
      throw new Error('invalid_partner_legal_document');
    }
    // Unveraenderte Dokumente behalten ihre eigene Version im aktiven Vertragspaket.
    if (document.type !== REQUIRED_PARTNER_LEGAL_TYPES[index]
        || !RELEASE_VERSION_RE.test(document.version)
        || !SHA256_RE.test(document.content_hash)
        || document.url !== `/api/v1/legal/${document.type}`) {
      throw new Error('invalid_partner_legal_document_evidence');
    }
    if (seen.has(document.type)) throw new Error('duplicate_partner_legal_document');
    seen.add(document.type);
    return { ...document };
  });
  return { ...payload, documents };
}

export default function Onboarding() {
  const { user, partner, logout, refreshPartner, isCurrentSession } = useAuth();
  const active = useRef(false);
  const owner = useRef(isCurrentSession);
  owner.current = isCurrentSession;
  const readSequence = useRef(0);
  useEffect(() => { active.current = true; return () => { active.current = false; readSequence.current += 1; }; }, []);
  const currentAttempt = useCallback(() => {
    const currentOwner = owner.current;
    return () => active.current && currentOwner();
  }, []);
  const [loadError, setLoadError] = useState(false);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agbChecked, setAgbChecked] = useState(false);
  const [legalBundle, setLegalBundle] = useState(null);
  const [legalLoading, setLegalLoading] = useState(true);
  const [legalError, setLegalError] = useState('');
  const [acceptError, setAcceptError] = useState('');
  const [uploading, setUploading] = useState({});
  const [bank, setBank] = useState({ iban: '', bic: '', bank_holder: '' });
  const [konz, setKonz] = useState({ issue_date: '', expires_at: '' });
  // DOKUMENT-ERKENNUNG-PARTNER-001: die Konzession wird ZUERST hochgeladen (gehalten + gelesen),
  // dann bestätigt der Partner gültig ab / gültig bis — erst das reicht sie zur Prüfung ein.
  const [konzHold, setKonzHold] = useState(null);
  const fileInputs = useRef({});

  const takeKonzHold = useCallback((hold) => {
    setKonzHold(hold || null);
    const f = (hold && hold.extraction && hold.extraction.fields) || {};
    setKonz((s) => ({ issue_date: s.issue_date || f.valid_from || '', expires_at: s.expires_at || f.valid_until || '' }));
  }, []);

  const loadLegalBundle = useCallback(async () => {
    const current = currentAttempt();
    setLegalLoading(true);
    setLegalError('');
    setAgbChecked(false);
    try {
      const response = await api.get('/legal/bundles/partner', { timeout: 10000 });
      if (!current()) return null;
      const bundle = normalizePartnerLegalBundle(response.data);
      setLegalBundle(bundle);
      return bundle;
    } catch (error) {
      if (!current()) return null;
      setLegalBundle(null);
      setLegalError('Die verbindlichen Partner-Vertragsunterlagen sind derzeit nicht vollständig verfügbar. Eine Annahme ist deshalb nicht möglich.');
      return null;
    } finally {
      if (current()) setLegalLoading(false);
    }
  }, [currentAttempt]);

  const load = useCallback(async () => {
    const own = currentAttempt();
    const sequence = ++readSequence.current;
    const current = () => own() && sequence === readSequence.current;
    setLoadError(false);
    try {
      const r = await api.get('/partners/onboarding', { timeout: 10000 });
      if (!current()) return null;
      setSt(r.data);
      const held = (r.data.holds || []).find((h) => h.document_type === 'konzession');
      if (held) takeKonzHold(held);
      return r.data;
    }
    catch { if (current()) { setSt(null); setLoadError(true); } return null; }
  }, [takeKonzHold, currentAttempt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLegalBundle(); }, [loadLegalBundle]);

  // Sanftes Polling: sobald der Admin freigibt, springt das Portal automatisch in den Vollzugriff.
  useEffect(() => {
    const t = setInterval(async () => {
      const current = currentAttempt();
      const s = await load();
      if (current() && s?.canOperate) refreshPartner();
    }, 20000);
    return () => clearInterval(t);
  }, [load, refreshPartner, currentAttempt]);

  const acceptAgb = async () => {
    const current = currentAttempt();
    if (!agbChecked || !legalBundle) return;
    setBusy(true);
    setAcceptError('');
    const documents = legalBundle.documents.map(({ type, version, content_hash }) => ({
      type,
      version,
      content_hash,
    }));
    try {
      const response = await api.post('/partners/accept-agb', { accepted: true, documents }, { timeout: 15000 });
      if (!current()) return;
      setSt(response.data);
      await refreshPartner();
      if (current()) toast.success('Vertragsunterlagen akzeptiert');
    } catch (error) {
      if (!current()) return;
      const code = error.response?.data?.error;
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') { setAcceptError('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.'); toast.error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.'); return; }
      setAgbChecked(false);
      if (code === 'stale_legal_bundle' || code === 'legal_bundle_ambiguous') {
        setAcceptError('Die Vertragsunterlagen wurden zwischenzeitlich geändert. Bitte prüfe die neu geladene Version und stimme erneut zu.');
        await loadLegalBundle();
      } else if (code === 'legal_bundle_unavailable') {
        setAcceptError('Die aktiven Vertragsunterlagen sind derzeit nicht verfügbar. Eine Annahme wurde nicht gespeichert.');
        await loadLegalBundle();
      } else {
        setAcceptError('Die Annahme konnte nicht beweissicher gespeichert werden. Bitte versuche es erneut.');
      }
      toast.error(code || 'Annahme fehlgeschlagen');
    }
    finally { if (current()) setBusy(false); }
  };
  const upload = async (documentType, file, extra = {}) => {
    const current = currentAttempt();
    if (!file) return;
    setUploading(u => ({ ...u, [documentType]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', documentType);
      if (extra.issue_date) form.append('issue_date', extra.issue_date);
      if (extra.expires_at) form.append('expires_at', extra.expires_at);
      await api.post('/partners/documents', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      if (!current()) return;
      if (current()) toast.success('Dokument hochgeladen');
      await load();
    } catch (e) {
      if (!current()) return;
      toast.error(e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' ? 'Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.' : (e.response?.data?.error || 'Upload fehlgeschlagen'));
    } finally {
      if (current()) setUploading(u => ({ ...u, [documentType]: false }));
    }
  };
  const recognizeKonzession = async (file) => {
    const current = currentAttempt();
    if (!file) return;
    setUploading(u => ({ ...u, konzession: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', 'konzession');
      const r = await api.post('/partners/documents/recognize', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      if (!current()) return;
      setKonz({ issue_date: '', expires_at: '' });   // neue Datei → neue Daten
      takeKonzHold(r.data.hold);
      const notice = extractionNotice(r.data.hold);
      if (notice) (notice.tone === 'ok' ? toast.success : toast)(notice.text);
    } catch (e) {
      if (!current()) return;
      toast.error(e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' ? 'Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.' : (e.response?.data?.error || 'Upload fehlgeschlagen'));
    } finally {
      if (current()) setUploading(u => ({ ...u, konzession: false }));
    }
  };
  const confirmKonzession = async () => {
    const current = currentAttempt();
    if (!konzHold) return;
    setBusy(true);
    try {
      await api.post('/partners/documents/confirm', { hold_id: konzHold.id, issue_date: konz.issue_date, expires_at: konz.expires_at }, { timeout: 15000 });
      if (!current()) return;
      if (current()) toast.success('Konzession eingereicht — sie wird geprüft');
      setKonzHold(null);
      setKonz({ issue_date: '', expires_at: '' });
      await load();
    } catch (e) {
      if (!current()) return;
      toast.error(e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' ? 'Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.' : (e.response?.data?.error || 'Bestätigung fehlgeschlagen'));
      if (e.response?.data?.reupload_required) { setKonzHold(null); await load(); }
    } finally { if (current()) setBusy(false); }
  };
  const saveBank = async () => {
    const current = currentAttempt();
    setBusy(true);
    try {
      await api.patch('/partners/me/bank', bank, { timeout: 15000 });
      if (!current()) return;
      if (current()) toast.success('Bankverbindung hinterlegt');
      await load();
    } catch (e) {
      if (!current()) return; toast.error(e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' ? 'Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.' : (e.response?.data?.error || 'Ungültige IBAN')); }
    finally { if (current()) setBusy(false); }
  };
  const refresh = async () => { const current = currentAttempt(); const s = await load(); if (!current() || !s) return; if (s.canOperate) await refreshPartner(); if (current()) toast.success('Status aktualisiert'); };

  if (!st) return <Centered>{loadError ? <div role="alert">Der aktuelle Status konnte nicht geladen werden.<button type="button" onClick={load}>Erneut versuchen</button><button type="button" onClick={logout}>Abmelden</button></div> : 'Wird geladen…'}</Centered>;

  const agbDone = st.agbAccepted;
  const docItems = st.documents || [];
  const legalReady = !!legalBundle && !legalLoading && !legalError;

  return (
    <div className="pp-onboarding-shell" style={{ minHeight: '100vh', background: '#f4f5f7' }}>
      {/* Kopf */}
      <header className="pp-onboarding-header" style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>V<span style={{ color: '#9C27B0' }}>OO</span>GO <span style={{ fontWeight: 600, color: '#4b5563', fontSize: 16 }}>Partner</span></div>
        <button onClick={logout} style={ghostBtn}><LogOut size={15} /> Abmelden</button>
      </header>

      <div className="pp-onboarding-content" style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 60px' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.5, marginBottom: 4 }}>Konto-Freischaltung</h1>
          <p style={{ color: '#4b5563', fontSize: 14 }}>
            {partner?.company_name} · Bitte die Partner-Vertragsunterlagen akzeptieren und die Pflicht-Dokumente hochladen.
            Nach Freigabe durch unser Team kannst du Fahrzeuge und Fahrer anlegen.
          </p>
        </div>

        {/* Status-Banner */}
        {st.step === 'review' && <Banner color="#4b5563" bg="#f3f4f6" border="#d1d5db" icon={<Clock size={16} color="#4b5563" />}>
          Alles eingereicht – dein Konto wird gerade geprüft. Du wirst freigeschaltet, sobald das Team bestätigt hat.
        </Banner>}
        {st.step === 'rejected' && <Banner color="#991b1b" bg="#fef2f2" border="#fca5a5" icon={<XCircle size={16} color="#dc2626" />}>
          Dein Antrag wurde abgelehnt{st.rejectionReason ? `: ${st.rejectionReason}` : ''}. Bitte korrigiere die Unterlagen und lade sie erneut hoch.
        </Banner>}

        {/* Schritt 1: Partner-Vertragsunterlagen */}
        <Card>
          <CardHead n="1" title="Vertragsunterlagen akzeptieren" done={agbDone} />
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, margin: '0 0 14px' }}>
            Vor der Freischaltung musst du die aktuell gültigen Partnerbedingungen, die Provisions- und Abrechnungsordnung sowie alle weiteren für Partner vorgeschriebenen Richtlinien prüfen.
          </p>
          {legalLoading && <div aria-live="polite" style={{ padding: 14, marginBottom: 14, background: '#f9fafb', borderRadius: 8, color: '#4b5563', fontSize: 13 }}>Verbindliche Vertragsunterlagen werden geladen …</div>}
          {legalError && (
            <div role="alert" style={{ padding: 14, marginBottom: 14, border: '1px solid #fca5a5', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, lineHeight: 1.5 }}>
              {legalError}
              <button type="button" onClick={loadLegalBundle} disabled={legalLoading} style={{ ...ghostBtn, display: 'flex', marginTop: 10, borderColor: '#fca5a5', color: '#991b1b' }}><RefreshCw size={14} /> Erneut laden</button>
            </div>
          )}
          {legalReady && (
            <div data-testid="partner-legal-bundle" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {legalBundle.release_version && <div style={{ fontSize: 12, color: '#4b5563' }}>Vertragspaket {legalBundle.release_version}</div>}
              {legalBundle.documents.map((document) => (
                <article key={document.type} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <strong style={{ color: '#374151', fontSize: 13 }}>{document.title}</strong>
                    <span style={{ color: '#4b5563', fontSize: 12 }}>Version {document.version}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href={document.url} target="_blank" rel="noreferrer" style={{ color: '#111', fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}>Speicherbare Fassung öffnen</a>
                    <span title={document.content_hash} style={{ color: '#6b7280', fontSize: 12 }}>Prüfsumme {document.content_hash.slice(0, 12)}…</span>
                  </div>
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#374151' }}>Inhalt hier anzeigen</summary>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.65, color: '#4b5563', maxHeight: 280, overflowY: 'auto' }}>{document.content}</div>
                  </details>
                </article>
              ))}
            </div>
          )}
          {acceptError && <div role="alert" style={{ marginBottom: 14, color: '#991b1b', fontSize: 13, lineHeight: 1.5 }}>{acceptError}</div>}
          {agbDone ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9C27B0', fontSize: 14, fontWeight: 600 }}>
              <CheckCircle size={16} /> Partner-Vertragsunterlagen akzeptiert
            </div>
          ) : (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={agbChecked} disabled={!legalReady || busy} onChange={e => setAgbChecked(e.target.checked)} style={{ width: 18, height: 18 }} />
                <span>{PARTNER_LEGAL_CONSENT_TEXT}</span>
              </label>
              <button onClick={acceptAgb} disabled={!agbChecked || busy || !legalReady} style={primaryBtn(!agbChecked || busy || !legalReady)}>
                Vertragsunterlagen akzeptieren & weiter
              </button>
            </>
          )}
        </Card>

        {/* Schritt 2: Dokumente */}
        <Card dim={!agbDone}>
          <CardHead n="2" title="Pflicht-Dokumente" done={st.docsComplete} />
          {!agbDone && <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Bitte zuerst die Partner-Vertragsunterlagen akzeptieren.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, opacity: agbDone ? 1 : 0.5, pointerEvents: agbDone ? 'auto' : 'none' }}>
            {docItems.map(item => {
              const b = badge[item.status] || badge.missing;
              return (
                <div key={item.type} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{item.label}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{b.icon}<span style={{ fontSize: 12, fontWeight: 600, color: b.color }}>{b.label}</span></div>
                  </div>

                  {item.type === 'bank' ? (
                    item.done ? (
                      <div style={{ fontSize: 13, color: '#4b5563' }}>IBAN + Nachweis hinterlegt <Check size={13} style={{ verticalAlign: -2 }} /> (Aktivierung nach Admin-Freigabe)</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <input placeholder="IBAN" value={bank.iban} onChange={e => setBank(s => ({ ...s, iban: e.target.value }))} style={{ ...inp, gridColumn: '1 / -1' }} />
                        <input placeholder="BIC (optional)" value={bank.bic} onChange={e => setBank(s => ({ ...s, bic: e.target.value }))} style={inp} />
                        <input placeholder="Kontoinhaber" value={bank.bank_holder} onChange={e => setBank(s => ({ ...s, bank_holder: e.target.value }))} style={inp} />
                        <button onClick={saveBank} disabled={busy} style={{ ...primaryBtn(busy), gridColumn: '1 / -1' }}>Bankverbindung speichern</button>
                        <label style={{ ...uploadBtn, gridColumn: '1 / -1' }}>
                          <Upload size={14} /> {uploading['bank_nachweis'] ? 'Lädt…' : 'Bank-/Inhabernachweis hochladen'}
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => { upload('bank_nachweis', e.target.files[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={!!uploading['bank_nachweis']} />
                        </label>
                      </div>
                    )
                  ) : item.type === 'konzession' ? (
                    /* DOKUMENT-ERKENNUNG-PARTNER-001: 1. hochladen (wird gelesen) · 2. Daten prüfen · 3. bestätigen */
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ ...uploadBtn, gridColumn: '1 / -1' }}>
                        <Upload size={14} /> {uploading['konzession'] ? 'Wird gelesen…' : (konzHold ? 'Andere Datei wählen' : 'Konzession hochladen')}
                        <input data-testid="onb-konz-upload" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" capture="environment"
                          onChange={e => { recognizeKonzession(e.target.files[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={!!uploading['konzession'] || busy} />
                      </label>
                      {konzHold ? (() => { const n = extractionNotice(konzHold); return (
                        <div data-testid="onb-konz-hold" style={{ gridColumn: '1 / -1', display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, fontWeight: 600, color: n.tone === 'ok' ? '#9C27B0' : '#4b5563' }}>
                          {n.tone === 'ok' ? <Sparkles size={13} style={{ flexShrink: 0, marginTop: 1 }} /> : <FileText size={13} style={{ flexShrink: 0, marginTop: 1 }} />}
                          <span>{konzHold.original_name || 'Datei'} · {n.text}</span>
                        </div>
                      ); })() : (
                        <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#6b7280' }}>Zuerst hochladen — Ausstellungs- und Ablaufdatum werden aus der Urkunde gelesen und hier zur Bestätigung angezeigt.</div>
                      )}
                      <label style={dateLbl}>Ausstellungsdatum
                        <input data-testid="onb-konz-issued" type="date" value={konz.issue_date} onChange={e => setKonz(s => ({ ...s, issue_date: e.target.value }))} style={inp} />
                      </label>
                      <label style={dateLbl}>Ablaufdatum
                        <input data-testid="onb-konz-expires" type="date" value={konz.expires_at} onChange={e => setKonz(s => ({ ...s, expires_at: e.target.value }))} style={inp} />
                      </label>
                      <button data-testid="onb-konz-confirm" onClick={confirmKonzession} disabled={busy || !konzHold || !konz.issue_date || !konz.expires_at}
                        style={{ ...primaryBtn(busy || !konzHold || !konz.issue_date || !konz.expires_at), gridColumn: '1 / -1' }}>
                        Konzession bestätigen
                      </button>
                      {konzHold && (!konz.issue_date || !konz.expires_at) && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#6b7280' }}>Bitte beide Daten prüfen bzw. eintragen, dann bestätigen.</div>}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {(UPLOADS[item.type] || [{ type: item.type, label: 'Hochladen' }]).map(u => (
                        <label key={u.type} style={uploadBtn}>
                          <Upload size={14} /> {uploading[u.type] ? 'Lädt…' : u.label}
                          <input ref={el => fileInputs.current[u.type] = el} type="file" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => { upload(u.type, e.target.files[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={!!uploading[u.type]} />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
          <button onClick={refresh} style={ghostBtn}><RefreshCw size={14} /> Status aktualisieren</button>
        </div>
      </div>
    </div>
  );
}

/* ── kleine UI-Bausteine ── */
const Card = ({ children, dim }) => (
  <div className="pp-onboarding-card" style={{ background: '#fff', borderRadius: 14, padding: 22, border: '1px solid #e5e7eb', marginBottom: 16, opacity: dim ? 0.96 : 1 }}>{children}</div>
);
const CardHead = ({ n, title, done }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    <div style={{ width: 26, height: 26, borderRadius: '50%', background: done ? '#9C27B0' : '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>
      {done ? <CheckCircle size={15} /> : n}
    </div>
    <h3 style={{ fontSize: 16, fontWeight: 800 }}>{title}</h3>
  </div>
);
const Banner = ({ children, color, bg, border, icon }) => (
  <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
    {icon}<span style={{ fontSize: 13, color, fontWeight: 600, lineHeight: 1.5 }}>{children}</span>
  </div>
);
const Centered = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280', fontSize: 14 }}>{children}</div>
);

const inp = { padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const dateLbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4b5563' };
const primaryBtn = (disabled) => ({ padding: '11px 18px', background: disabled ? '#d1d5db' : '#000', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' });
const uploadBtn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: '#f4f5f7', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' };
